import React from "react";
import { Button, Typography, Stack, Alert, CircularProgress } from "@mui/material";
import { ArrowBack } from "@mui/icons-material";
import ReferenceBuilder from "./ReferenceBuilder";
import { DocumentReference } from "../../../utils/taskpane";
import { useGetReferencesQuery } from "../api/playbookApi";
import { AssemblyType } from "../types/assembly-model";
import { AssemblyReferenceModel, AssemblyReferenceWriteModel } from "../types/assembly-reference";
import { useAssemblyContext } from "../context/AssemblyContext";

interface EditReferenceViewProps {
  assemblyType: AssemblyType;
  assignedGroupObjectIdentifier?: string;
  selectedReference: DocumentReference;
  onBack: () => void;
  onUpdateReference: (
    model: AssemblyReferenceWriteModel,
    promptLabel: string,
    existing: AssemblyReferenceModel
  ) => Promise<void>;
  onDeleteReference: (existing: AssemblyReferenceModel) => Promise<void>;
  loading: boolean;
  error: boolean;
}

const EditReferenceView = ({
  assemblyType,
  assignedGroupObjectIdentifier,
  selectedReference,
  onBack,
  onUpdateReference,
  onDeleteReference,
  loading,
  error,
}: EditReferenceViewProps) => {
  const ctx = useAssemblyContext();
  const { data: references, isLoading, error: refError } = useGetReferencesQuery({ ctx });

  // The Word SDT is tagged with the stable identifier; resolve the full
  // reference (which carries the API objectIdentifier) from the list.
  const referenceData = references?.find(
    (r) => r.stableObjectIdentifier === selectedReference.stableObjectIdentifier
  );

  return (
    <Stack p={2} spacing={2} direction="column">
      <Stack direction="row" alignItems="center">
        <Button
          startIcon={<ArrowBack fontSize="small" />}
          variant="text"
          sx={{ color: "text.secondary" }}
          onClick={onBack}
          size="small"
        >
          Back
        </Button>
      </Stack>

      {isLoading ? (
        <Stack alignItems="center" p={2}>
          <CircularProgress />
          <Typography variant="body2" sx={{ mt: 2 }}>
            Loading reference data...
          </Typography>
        </Stack>
      ) : refError ? (
        <Alert severity="error">Failed to load reference data. Please try again.</Alert>
      ) : !referenceData ? (
        <Alert severity="warning">
          This reference has been deleted and is no longer available for editing.
        </Alert>
      ) : (
        <ReferenceBuilder
          assemblyType={assemblyType}
          assignedGroupObjectIdentifier={assignedGroupObjectIdentifier}
          onSubmit={(model, promptLabel) => onUpdateReference(model, promptLabel, referenceData)}
          onDelete={() => onDeleteReference(referenceData)}
          loading={loading}
          error={error}
          onClose={onBack}
          existingReference={referenceData}
          isEditMode
        />
      )}
    </Stack>
  );
};

export default EditReferenceView;
